import React, { useState, useRef, useEffect } from 'react';
import { Pencil, Trash2, Clock, ThumbsUp, GripVertical } from 'lucide-react';
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CategoryWithMonthData } from '../types/category';
import { usePlans } from '../contexts/PlanContext';
import { useCategories } from '../contexts/CategoryContext';
import { Notification, NotificationType } from './Notification';
import { MoneyInput } from './MoneyInput';
import { categoryService } from '../services/categoryService';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

interface SubCategoryRowProps {
  category: CategoryWithMonthData;
  onEdit: () => void;
  onDelete: () => void;
  isUnderSystemGroup?: boolean;  // True if this category is under a CC/Loan system group
  linkedAccountBillingDate?: number;  // Billing date (1-28) if under CC/Paylater system group
  linkedAccountId?: string;  // Account ID if under CC/Paylater system group
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isDragging?: boolean;
}

// PERFORMANCE: Memoize SubCategoryRow to prevent unnecessary re-renders
export const SubCategoryRow: React.FC<SubCategoryRowProps> = React.memo(({
  category,
  onEdit,
  onDelete,
  isUnderSystemGroup = false,
  linkedAccountBillingDate,
  dragHandleProps,
  isDragging = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentMonthPlan } = usePlans();
  const { formatMoney } = useUserPreferences();
  // PERFORMANCE: Use context functions for optimistic updates without full page reload
  const { updateCategoryAllocatedLocally, refreshBudgetStatusOnly } = useCategories();

  // Goal-based saving detection and values
  const isGoal = category.is_goal || false;
  const goalTarget = category.goal_target || 0;
  const goalMonthlyTarget = category.goal_monthly_target; // Backend-calculated monthly target
  const goalTargetMonth = category.goal_target_month;
  const goalSaved = category.goal_saved || 0;

  const allocated = category.allocated || 0;
  const transaction = category.transaction || 0;
  const balance = allocated - transaction;

  // Toggle between showing transaction or balance
  const [showBalance, setShowBalance] = useState(false);

  // Inline editing state (only for non-goal categories)
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [editValue, setEditValue] = useState<number>(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const editValueRef = useRef<number>(0);

  // Notification state
  const [notification, setNotification] = useState<{ type: NotificationType; message: string } | null>(null);

  // Desktop detection (lg: 1024px+)
  const [isDesktop, setIsDesktop] = useState(false);
  
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Display allocated value from context (updated optimistically via updateCategoryAllocatedLocally)
  const displayAllocated = allocated;

  // Dynamic labels based on category type
  // Priority: Goal > System Group (CC/Loan) > Normal
  const transactionLabel = isGoal ? t('subcategory_row.saved') : (isUnderSystemGroup ? t('subcategory_row.payment') : t('subcategory_row.used'));
  const balanceLabel = isGoal ? t('subcategory_row.remaining_target') : (isUnderSystemGroup ? t('subcategory_row.bill') : t('subcategory_row.remaining'));

  // Keep editValueRef in sync with editValue
  useEffect(() => {
    editValueRef.current = editValue;
  }, [editValue]);

  // Handle click outside:
  // - Desktop: Cancel edit (user must press Enter to save)
  // - Mobile: Save edit (user clicks checkmark on custom keyboard)
  useEffect(() => {
    if (!isEditingBudget) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        if (isDesktop) {
          // Desktop: Click outside cancels
          handleCancelEdit();
        } else {
          // Mobile: Click outside saves (same as before)
          const currentValue = editValueRef.current;
          handleSaveValue(currentValue);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingBudget, isDesktop]); // Re-setup when editing state or desktop detection changes

  // Handle clicking on allocated amount to start editing
  const handleBudgetClick = () => {
    // For goals WITH target month, open the edit modal
    if (isGoal && goalTargetMonth) {
      onEdit();
      return;
    }

    // For goals WITHOUT target month and normal categories, enable inline editing
    setEditValue(displayAllocated);
    setIsEditingBudget(true);
  };

  // Handle money input value change
  // NOTE: With custom keyboard (mobile), this is ONLY called when user confirms (clicks checkmark)
  // On desktop without custom keyboard, this is called on every change
  const handleMoneyChange = (value: number) => {
    if (isDesktop) {
      // Desktop: Just update the value, don't save yet (save on Enter)
      setEditValue(value);
    } else {
      // Mobile: Save immediately when custom keyboard confirms (checkmark clicked)
      setEditValue(value);
      handleSaveValue(value);
    }
  };

  // Extract save logic to reusable function
  const handleSaveValue = (currentValue: number) => {
    if (!category.category_month_id) {
      setNotification({ type: 'error', message: t('subcategory_row.cannot_update_allocation') });
      setIsEditingBudget(false);
      return;
    }

    if (isNaN(currentValue) || currentValue < 0) {
      setNotification({ type: 'error', message: t('subcategory_row.invalid_amount') });
      setIsEditingBudget(false);
      return;
    }

    if (currentValue === allocated) {
      setIsEditingBudget(false);
      return;
    }

    // PERFORMANCE: Optimistic update via context (updates category list immediately)
    updateCategoryAllocatedLocally(category.category_month_id!, currentValue);
    setIsEditingBudget(false);

    // Prepare update payload
    const updatePayload: any = {
      allocated: currentValue,
      same_allocation: category.same_allocation,
    };

    // For goals without target month, also update goal_monthly_target
    if (isGoal && !goalTargetMonth) {
      updatePayload.goal_monthly_target = currentValue;
    }

    categoryService.updateCategoryMonth(category.category_month_id, updatePayload).then(() => {
      // PERFORMANCE: Only refresh budget status, not the whole page
      refreshBudgetStatusOnly();
    }).catch((error: any) => {
      console.error('Error updating budget:', error);
      // Rollback optimistic update by reverting to original value
      updateCategoryAllocatedLocally(category.category_month_id!, allocated);
      setNotification({
        type: 'error',
        message: error.response?.data?.detail || t('subcategory_row.update_failed'),
      });
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditingBudget(false);
  };

  // Handle keyboard events for MoneyInput (only for native keyboard, not custom keyboard)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Use latest value from ref
      const currentValue = editValueRef.current;
      handleSaveValue(currentValue);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  // Navigate to transaction page with filters
  const handleViewTransactions = () => {
    if (!currentMonthPlan) return;

    let dateFrom: Date;
    let dateTo: Date;

    // Check if this is under a CC/Paylater system group with billing date
    if (isUnderSystemGroup && linkedAccountBillingDate) {
      // Parse month string directly
      const [year, month] = currentMonthPlan.month.split('-').map(Number);

      // Extended range: previous billing date to NEXT month's billing date
      // This accounts for payments made after the billing date
      const previousBillingDate = new Date(year, month - 2, linkedAccountBillingDate);
      const nextBillingDate = new Date(year, month, linkedAccountBillingDate); // Next month

      dateFrom = previousBillingDate;
      dateTo = nextBillingDate;
    } else {
      // Normal category - get last day of the month
      const [yearStr, monthStr] = currentMonthPlan.month.split('-');
      const year = parseInt(yearStr);
      const month = parseInt(monthStr);

      // Get last day of the month
      const lastDay = new Date(year, month, 0).getDate();

      dateFrom = new Date(year, month - 1, lastDay);
      dateFrom.setDate(dateFrom.getDate() - 60);
      dateTo = new Date(year, month - 1, lastDay);
    }

    // Format dates as YYYY-MM-DD without timezone conversion
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Navigate with state containing filter parameters
    // Note: Do NOT filter by account for CC/Paylater categories - show all transactions
    const navigationState: any = {
      categoryId: category.id,
      categoryName: category.name, // Pass name to match original transactions
      monthPlanId: currentMonthPlan.id, // Pass month plan ID for Anggaran Bulan filter
      dateFrom: formatDate(dateFrom),
      dateTo: formatDate(dateTo),
    };

    navigate('/history', { state: navigationState });
  };

  // Calculate progress percentage (capped at 100% for display)
  // For goals WITHOUT target month: use goal_saved (accumulated across all months)
  // For goals WITH target month: use transaction (current month only)
  // For normal categories: use transaction vs allocated
  let progressPercentage = 0;
  if (isGoal) {
    // Goal progress calculation
    const targetForProgress = goalMonthlyTarget || goalTarget;

    if (goalTargetMonth) {
      // Goals WITH target month: show progress for THIS MONTH only
      progressPercentage = targetForProgress > 0 ? (transaction / targetForProgress) * 100 : 0;
    } else {
      // Goals WITHOUT target month: show accumulated progress
      progressPercentage = targetForProgress > 0 ? (goalSaved / targetForProgress) * 100 : 0;
    }
  } else {
    // Normal category progress: transaction vs allocated
    progressPercentage = displayAllocated > 0 ? (transaction / displayAllocated) * 100 : 0;
  }
  const isOverspent = transaction > allocated;

  // Determine progress bar color
  // Important: Goals should NEVER turn red when exceeding target (saving more is good!)
  const getProgressColor = () => {
    if (isGoal) {
      // Goals are always green - saving more than planned is positive!
      return 'bg-emerald-500';
    }
    if (isOverspent) return 'bg-red-500'; // Over budget
    return 'bg-emerald-500'; // On track
  };

  // Helper function to create diagonal line pattern
  const getDiagonalLinesStyle = (baseColor: string) => {
    return {
      backgroundImage: `repeating-linear-gradient(
        45deg,
        ${baseColor},
        ${baseColor} 2px,
        transparent 2px,
        transparent 4px
      )`
    };
  };

  // Container background color (grey when empty, light green otherwise)
  const containerBgColor = transaction === 0 ? 'bg-gray-100' : 'bg-gray-100';

  // Check if bar is full (100%) but not overspent
  const isFull = progressPercentage >= 100 && !isOverspent;

  // Check if goal has reached or exceeded target
  const isGoalAchieved = isGoal && progressPercentage >= 100;

  const formatGoalTargetMonth = (goalTargetMonth: string | null | undefined) => {
    if (!goalTargetMonth) return '';
    // Add a dummy day to make it a valid date string
    const date = new Date(`${goalTargetMonth}-01`);
    return date.toLocaleString('en-US', { month: 'short', year: '2-digit' });
  };

  return (
    <div className={`p-2.5 bg-white ${isDragging ? 'opacity-50' : ''}`}>
      {/* First row: Drag Handle, Category name with badge and Action buttons */}
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 flex-1">
          {/* Drag Handle */}
          <div
            {...dragHandleProps}
            className="cursor-grab active:cursor-grabbing flex-shrink-0"
            style={{ transition: 'none' }}
          >
            <GripVertical size={14} className="text-[#077f5d]" />
          </div>
          <span className="text-[#1a1a1a] font-bold text-sm text-left">{category.name}</span>
          {category.is_installment && (
            <span
              className="px-1 py-0.5 bg-[#dffbd0] text-[#077f5d] border-0 border-[#077f5d] font-bold"
              style={{ fontSize: '10px' }}
            >
              {category.installment_progress}/{category.installment_term}
            </span>
          )}
          {isGoal && goalMonthlyTarget !== null && (
            <span
              className="px-1 py-0.5 bg-[#dffbd0] text-[#077f5d] border-0 border-[#077f5d] font-bold"
              style={{ fontSize: '10px' }}
            >
              {formatGoalTargetMonth(goalTargetMonth)}
            </span>
          )}
        </div>

        {/* Right: Action buttons */}
        <div className="flex gap-1 flex-shrink-0">
          <button
            onClick={handleViewTransactions}
            className="p-0.5 text-[#077f5d] hover:bg-[#dffbd0] border-2 border-transparent hover:border-[#077f5d]"
            style={{ transition: 'none' }}
            title={t('subcategory_row.view_transactions')}
          >
            <Clock size={14} />
          </button>
          <button
            onClick={onEdit}
            className="p-0.5 text-[#077f5d] hover:bg-[#dffbd0] border-2 border-transparent hover:border-[#077f5d]"
            style={{ transition: 'none' }}
            title={t('subcategory_row.edit')}
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={onDelete}
            className="p-0.5 text-[#077f5d] hover:bg-[#dffbd0] border-2 border-transparent hover:border-[#077f5d]"
            style={{ transition: 'none' }}
            title={t('subcategory_row.delete')}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Second row: Transaction/Balance (left) and Allocated (right) - full width */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setShowBalance(!showBalance)}
          className="text-xs hover:text-[#3cbe8c] cursor-pointer font-mono text-left flex-1 pr-1"
          style={{ transition: 'none' }}
        >
          {showBalance ? (
            <span
              className={
                isGoal && goalTarget <= goalSaved
                  ? 'text-green-600 font-bold'
                  : balance < 0 && !isGoal
                    ? 'text-red-600 font-bold'
                    : 'text-[#1a1a1a]'
              }
            >
              {isGoal && goalTarget <= goalSaved ? (
                <>
                  {transactionLabel}: {formatMoney(goalSaved)}
                </>
              ) : (
                <>
                  {balanceLabel}:{' '}
                  {isGoal
                    ? formatMoney(goalTarget - goalSaved)
                    : formatMoney(balance)}
                </>
              )}
            </span>
          ) : (
            <span
              className={
                isGoal && goalTarget <= goalSaved
                  ? 'text-green-600 font-bold'
                  : isOverspent && !isGoal
                    ? 'text-red-600 font-bold'
                    : 'text-[#1a1a1a]'
              }
            >
              {isGoal && goalTarget <= goalSaved ? (
                <span className="inline-flex items-center gap-1 text-green-600 font-bold">
                  {t('subcategory_row.achieved')} <ThumbsUp size={14} />
                </span>
              ) : (
                <>
                  {transactionLabel}:{' '}
                  {isGoal
                    ? goalTargetMonth
                      ? formatMoney(transaction)
                      : formatMoney(goalSaved)
                    : formatMoney(transaction)}
                </>
              )}
            </span>
          )}
        </button>
        {/* Allocated / Target (right side, same line) */}
        {isGoal && goalTargetMonth ? (
          // Goal with target month: Click opens edit modal
          <button
            onClick={handleBudgetClick}
            className="inline-flex font-mono font-bold text-xs items-center bg-[#dffbd0] text-[#077f5d] px-2 py-0 border-2 border-[#077f5d] flex-shrink-0 hover:bg-[#cdf7bd] cursor-pointer"
            style={{ transition: 'none' }}
            title={t('subcategory_row.click_edit_goal')}
          >
            <>{formatMoney(goalMonthlyTarget || 0)}</>
          </button>
        ) : isGoal && !goalTargetMonth && !isEditingBudget ? (
          // Goal without target month: Click enables inline editing (show goal_monthly_target)
          <button
            onClick={handleBudgetClick}
            className="inline-flex font-mono font-bold text-xs items-center bg-[#dffbd0] text-[#077f5d] px-2 py-0 border-2 border-[#077f5d] flex-shrink-0 hover:bg-[#cdf7bd] cursor-pointer"
            style={{ transition: 'none' }}
            title={t('subcategory_row.click_change_monthly_saving')}
          >
            <>{formatMoney(goalMonthlyTarget || displayAllocated)}</>
          </button>
        ) : isEditingBudget ? (
          // Normal category in edit mode: Inline MoneyInput
          <div
            ref={wrapperRef}
            className="inline-flex items-center bg-[#dffbd0] text-[#077f5d] px-1 py-0 border-2 border-[#077f5d] flex-shrink-0"
            onKeyDown={handleKeyDown}
          >
            <MoneyInput
              value={editValue}
              onValueChange={handleMoneyChange}
              autoFocus={true}
              className="font-mono font-bold text-xs bg-white text-[#077f5d] border-2 border-[#077f5d] px-1 py-0.5 w-32 focus:outline-none focus:ring-2 focus:ring-[#3cbe8c]"
              style={{ transition: 'none' }}
              placeholder={formatMoney(0)}
              useCustomKeyboard={!isDesktop}
              keyboardTitle={isGoal ? t('subcategory_row.monthly_saving_plan') : t('subcategory_row.allocation')}
            />
          </div>
        ) : (
          // Normal category: Click enables inline editing
          <button
            onClick={handleBudgetClick}
            className="inline-flex font-mono font-bold text-xs items-center bg-[#dffbd0] text-[#077f5d] px-2 py-0 border-2 border-[#077f5d] flex-shrink-0 hover:bg-[#cdf7bd] cursor-pointer"
            style={{ transition: 'none' }}
            title={t('subcategory_row.click_change_allocation')}
          >
            {formatMoney(displayAllocated)}
          </button>
        )}
      </div>

      {/* Third row: Progress bar - full width */}
      <div className={`h-2 ${containerBgColor} border-2 border-[#077f5d] flex`}>
        {!isGoal && isOverspent ? (
          // Non-goal overspent: Two segments inside the bar
          // First segment: allocated portion with dark green diagonal lines
          // Second segment: overspent portion with red diagonal lines
          <>
            <div
              className="h-full bg-emerald-500"
              style={{
                width: `${(allocated / transaction) * 100}%`,
                transition: 'none',
                ...getDiagonalLinesStyle('#077f5d')
              }}
            />
            <div
              className="h-full bg-[#dc2626]"
              style={{
                width: `${((transaction - allocated) / transaction) * 100}%`,
                transition: 'none',
                ...getDiagonalLinesStyle('#991b1b')
              }}
            />
          </>
        ) : isGoalAchieved ? (
          // Goal achieved/exceeded: Green with dark green diagonal lines (saving more is good!)
          <div
            className="h-full bg-emerald-500"
            style={{
              width: '100%',
              transition: 'none',
              ...getDiagonalLinesStyle('#077f5d')
            }}
          />
        ) : !isGoal && isFull ? (
          // Non-goal full bar (100%): Green with dark green diagonal lines
          <div
            className="h-full bg-emerald-500"
            style={{
              width: '100%',
              transition: 'none',
              ...getDiagonalLinesStyle('#077f5d')
            }}
          />
        ) : (
          // Normal progress: Keep current behavior
          <div
            className={`h-full ${getProgressColor()}`}
            style={{ width: `${Math.min(progressPercentage, 100)}%`, transition: 'none' }}
          />
        )}
      </div>

      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
          duration={notification.type === 'success' ? 2000 : 3000}
        />
      )}
    </div>
  );
}); // End React.memo
